/**
 * One popover menu — anchored to a button OR to a point (right-click).
 *
 * PROMOTED INTO ui-kit 2026-08-05. It was isg-boardroom's, and by then a THIRD
 * implementation had appeared in isg-hr's org chart editor — whose teardown
 * used `{once: true}` listeners, so a menu dismissed with Escape left one
 * armed and it tore down the NEXT menu as it opened. Right-click worked
 * exactly once per page load. This version's capture-phase teardown is what
 * gets that right, which is the argument for there being one of these.
 *
 * isg-marketing's (`campaigns.js showCampaignContextMenu`) is still welded
 * into that page — it closes over `allCampaigns` and only ever describes
 * campaigns. Convert it when that page is next opened for its own reasons.
 *
 * Both are the same thing: a list of actions floated over the page, dismissed by
 * outside-click or Esc, clamped inside the viewport. The only difference is what
 * it is anchored to. So they are one implementation with two entry points
 * (`attachExportMenu`, `attachContextMenu`) rather than two popovers that drift.
 *
 * isg-marketing's context menu (campaigns.js `showCampaignContextMenu`) is the
 * reference for the behaviour — point anchor, viewport clamp, panel drill-down,
 * capture-phase dismissal. It could not be imported: it is welded into that page,
 * closing over `allCampaigns` and `ctxMenuCampaignId`, so it only ever describes
 * campaigns. What is reused here is its SHAPE, made generic.
 *
 * ## Items
 *
 *   { label, hint?, run() }               — an action
 *   { label, panel: [ …items ] }          — drill into a submenu
 *   { separator: true }                   — a rule
 *   { heading: 'Move to' }                — a non-clickable label
 *
 * `run` may be async; a throw surfaces as a toast rather than an unhandled
 * rejection, because these fire API calls and a silent failure on "mark done"
 * is worse than an error.
 */
// ⚠️ Relative imports, not '@isg/ui-kit'. Inside the package that is a
// circular self-import — it resolves at build time in a consuming app and
// fails outright anywhere the package is used on its own.
import '../../css/menu.css';
import { showError } from './toast.js';
import { t } from '../i18n/index.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

let openEl = null;
let anchorPt = null;
let panelStack = [];

function teardown() {
  openEl?.remove();
  openEl = null;
  anchorPt = null;
  panelStack = [];
  document.removeEventListener('click', onOutside, true);
  document.removeEventListener('keydown', onKey, true);
  window.removeEventListener('resize', reposition);
}
function onOutside(e) { if (openEl && !openEl.contains(e.target)) teardown(); }
function onKey(e) {
  if (e.key !== 'Escape' || !openEl) return;
  // Esc walks BACK out of a submenu before closing — otherwise drilling in and
  // changing your mind throws the whole menu away.
  if (panelStack.length > 1) { panelStack.pop(); paint(); return; }
  teardown();
}

/** Clamp inside the viewport. Reset to the anchor first, or the measurement is
 *  of the PREVIOUS clamped position and the menu creeps on each repaint. */
function reposition() {
  if (!openEl || !anchorPt) return;
  const m = 8;
  openEl.style.left = `${anchorPt.x}px`;
  openEl.style.top = `${anchorPt.y}px`;
  const r = openEl.getBoundingClientRect();
  openEl.style.left = `${Math.max(m, Math.min(anchorPt.x, window.innerWidth - r.width - m))}px`;
  openEl.style.top = `${Math.max(m, Math.min(anchorPt.y, window.innerHeight - r.height - m))}px`;
}

function paint() {
  const items = panelStack[panelStack.length - 1];
  const deep = panelStack.length > 1;
  openEl.innerHTML = `
    ${deep ? `<button class="brx-menu-item brx-menu-back" data-back="1"><span class="brx-menu-label">← ${escapeHtml(t('menu.back'))}</span></button><div class="brx-menu-sep"></div>` : ''}
    ${items.map((o, i) => {
      if (o.separator) return '<div class="brx-menu-sep"></div>';
      if (o.heading) return `<div class="brx-menu-heading">${escapeHtml(o.heading)}</div>`;
      return `<button class="brx-menu-item" data-i="${i}"${o.disabled ? ' disabled' : ''}>
        <span class="brx-menu-label">${escapeHtml(o.label)}${o.panel ? ' ›' : ''}</span>
        ${o.hint ? `<span class="brx-menu-hint">${escapeHtml(o.hint)}</span>` : ''}
      </button>`;
    }).join('')}`;

  openEl.querySelector('[data-back]')?.addEventListener('click', (e) => {
    e.stopPropagation(); panelStack.pop(); paint();
  });
  openEl.querySelectorAll('.brx-menu-item[data-i]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const o = items[Number(el.dataset.i)];
      if (o.panel) { panelStack.push(o.panel); paint(); return; }
      teardown();
      try { await o.run?.(); }
      catch (err) { showError(err?.message || t('menu.action_failed')); }
    });
  });
  reposition();
}

function openAt(x, y, items) {
  teardown();
  openEl = document.createElement('div');
  openEl.className = 'brx-menu';
  document.body.appendChild(openEl);
  anchorPt = { x, y };
  panelStack = [items];
  paint();
  // capture:true — a card's own click handler calls stopPropagation, so a
  // bubble-phase listener would never see the click that should dismiss us.
  // Deferred a tick so the opening click doesn't immediately close it.
  setTimeout(() => {
    document.addEventListener('click', onOutside, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', reposition);
  }, 0);
}

/** Button-anchored (Export ▾). Opens under the trigger. */
export function attachExportMenu(btn, items) {
  if (!btn) return btn;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (openEl) { teardown(); return; }
    const r = btn.getBoundingClientRect();
    openAt(r.left, r.bottom + 6, typeof items === 'function' ? items() : items);
  });
  return btn;
}

/**
 * Point-anchored (right-click).
 *
 * `itemsFor(el)` is called at CLICK time, not at bind time, so the menu reflects
 * the row's state right now — a card whose status changed since render must not
 * offer a stale "Mark complete".
 *
 * Delegated from a container rather than bound per row: the board and list are
 * re-rendered constantly (every filter keystroke), and per-node bindings would
 * be thrown away and re-added on each repaint.
 */
export function attachContextMenu(container, selector, itemsFor) {
  container.addEventListener('contextmenu', (e) => {
    const el = e.target.closest(selector);
    if (!el || !container.contains(el)) return;
    // Leave the NATIVE menu alone on inputs and links — right-click there means
    // copy/paste/open-in-new-tab, and hijacking that is user-hostile.
    if (e.target.closest('input, textarea, select, a, [contenteditable]')) return;
    const items = itemsFor(el);
    if (!items || !items.length) return;
    e.preventDefault();
    openAt(e.clientX, e.clientY, items);
  });
}

export const exportButtonHtml = (id) =>
  `<button class="btn btn-secondary btn-sm" id="${id}" title="${escapeHtml(t('menu.export_title'))}">⬇ ${escapeHtml(t('menu.export'))} ▾</button>`;

export const closeMenu = teardown;
